import { appConfig } from '@config'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'

import {
  FIELD_BIND_TYPE_ENUM,
  MAP_DEFAULT_PROVIDER,
  MAP_POINT_GROUP_LABEL_ALIGNMENT,
  MAP_POINT_GROUP_LABEL_POSITION,
  MAP_STATION_CODE,
  MAP_STATION_DEFAULTS,
  MapStationDefaults,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import { MapConfig } from '@types'

import { MapFormValues } from '../types'

const getStationDefaults = (): MapStationDefaults => {
  const stationCode = (appConfig.mapStationCode as MAP_STATION_CODE) || MAP_STATION_CODE.VKO

  return MAP_STATION_DEFAULTS[stationCode] ?? MAP_STATION_DEFAULTS[MAP_STATION_CODE.VKO]
}

export const convertResponseToFormData = (data: MapConfig): MapFormValues => {
  const defaults = getStationDefaults()

  return {
    id: data.id,
    code: data.code,
    title: data.title,
    mapSettings: {
      center: {
        longitude: data.mapSettings.center[0],
        latitude: data.mapSettings.center[1],
      },
      zoom: data.mapSettings.zoom,
      maxBounds: {
        swLatitude: data.mapSettings.maxBounds?.[0][1] || defaults.swLatitude,
        swLongitude: data.mapSettings.maxBounds?.[0][0] || defaults.swLongitude,
        neLatitude: data.mapSettings.maxBounds?.[1][1] || defaults.neLatitude,
        neLongitude: data.mapSettings.maxBounds?.[1][0] || defaults.neLongitude,
      },
    },
    mapProvider: data.mapProvider,
    pointGroups: data.pointGroups?.length
      ? data.pointGroups.map(pointGroup => {
          return {
            id: pointGroup.id,
            title: pointGroup.title,
            objectCode: {
              id: pointGroup.objectCode,
              label: pointGroup.objectCode, // TODO objectTitle
            },
            coordinateFields: {
              latitudeField: {
                id: pointGroup.coordinateFields?.latitudeField,
                label: pointGroup.coordinateFields?.latitudeField,
              },
              longitudeField: {
                id: pointGroup.coordinateFields?.longitudeField,
                label: pointGroup.coordinateFields?.longitudeField,
              },
            },
            label: {
              bindType: {
                id: pointGroup.label?.bindType || FIELD_BIND_TYPE_ENUM.JS,
                label: pointGroup.label?.bindType || FIELD_BIND_TYPE_ENUM.JS,
              },
              position: {
                id: pointGroup.label?.position || MAP_POINT_GROUP_LABEL_POSITION.BOTTOM,
                label: pointGroup.label?.position || MAP_POINT_GROUP_LABEL_POSITION.BOTTOM,
              },
              align: {
                id: pointGroup.label?.align || MAP_POINT_GROUP_LABEL_ALIGNMENT.CENTER,
                label: pointGroup.label?.align || MAP_POINT_GROUP_LABEL_ALIGNMENT.CENTER,
              },
              value: pointGroup.label?.value || '',
            },
            pointStyle: {
              color: {
                bindType: {
                  id: pointGroup.pointStyle.color?.bindType || FIELD_BIND_TYPE_ENUM.STATIC,
                  label: pointGroup.pointStyle.color?.bindType || FIELD_BIND_TYPE_ENUM.STATIC,
                },
                value: pointGroup.pointStyle?.color?.value,
              },
            },
            getDataCommand: {
              ...pointGroup.getDataCommand,
              internalId: pointGroup.getDataCommand?.internalId as string,
              name: {
                id: pointGroup.getDataCommand?.name as string,
                label: pointGroup.getDataCommand?.name as string,
                httpMethod: pointGroup.getDataCommand?.httpMethod,
              },
              objectCode: {
                id: pointGroup.getDataCommand?.objectCode as string,
                label: pointGroup.getDataCommand?.objectTitle as string,
              },
              type: {
                id: pointGroup.getDataCommand?.type as string,
                label: pointGroup.getDataCommand?.type as string,
              },
              parameters: getStateFromData(pointGroup.getDataCommand?.parameters),
              body: getStateFromData(pointGroup.getDataCommand?.body),
            },
          }
        })
      : [],
  }
}

export const convertFormDataToRequest = (data: MapFormValues): MapConfig => {
  return {
    id: data.id,
    code: data.code,
    title: data.title,
    mapProvider: data.mapProvider,
    mapSettings: {
      center: [data.mapSettings.center.longitude, data.mapSettings.center.latitude],
      zoom: data.mapSettings.zoom,
      maxBounds: [
        [data.mapSettings.maxBounds.swLongitude, data.mapSettings.maxBounds.swLatitude],
        [data.mapSettings.maxBounds.neLongitude, data.mapSettings.maxBounds.neLatitude],
      ],
    },
    pointGroups: data.pointGroups.map(pointGroup => {
      return {
        id: pointGroup.id,
        title: pointGroup.title,
        objectCode: pointGroup.objectCode?.id as string,
        coordinateFields: {
          latitudeField: pointGroup.coordinateFields.latitudeField?.id as string,
          longitudeField: pointGroup.coordinateFields.longitudeField?.id as string,
        },
        pointStyle: {
          color: {
            value: pointGroup.pointStyle.color.value,
            bindType: pointGroup.pointStyle.color.bindType?.id as FIELD_BIND_TYPE_ENUM,
          },
        },
        getDataCommand: {
          internalId: '',
          name: pointGroup.getDataCommand?.name?.id as string,
          httpMethod: pointGroup.getDataCommand?.name?.httpMethod as string,
          resource: TYPES_SOURCE_OPTIONS.OBJECT,
          objectCode: pointGroup.objectCode?.id as string,
          objectTitle: pointGroup.objectCode?.label as string,
          parameters: getDataFromState(pointGroup.getDataCommand?.parameters),
          body: getDataFromState(pointGroup.getDataCommand?.body),
        },
        label: pointGroup.label
          ? {
              bindType: pointGroup.label.bindType?.id as FIELD_BIND_TYPE_ENUM,
              position: pointGroup.label.position?.id,
              align: pointGroup.label.align?.id,
              value: pointGroup.label.value,
            }
          : undefined,
      }
    }),
  }
}

export const getDefaultValues = (): MapFormValues => {
  const defaults = getStationDefaults()

  return {
    code: '',
    title: '',
    mapProvider: MAP_DEFAULT_PROVIDER,
    mapSettings: {
      zoom: defaults.zoom,
      center: {
        latitude: defaults.centerLatitude,
        longitude: defaults.centerLongitude,
      },
      maxBounds: {
        neLatitude: defaults.neLatitude,
        neLongitude: defaults.neLongitude,
        swLatitude: defaults.swLatitude,
        swLongitude: defaults.swLongitude,
      },
    },
    pointGroups: [],
  }
}
